import { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth, formatApiErrorDetail } from '../context/AuthContext';
import { resolveDestination } from '../utils/safeRedirect';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Terminal, Eye, EyeOff } from 'lucide-react';

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const user = await login(email, password);
      // Phase 1 (Feb 2026) — Redirect-aware login. Deep-link `?next=` (from
      // email/push) or `state.from` (from ProtectedRoute bounce) wins over
      // the role dashboard, but only when same-origin AND the target path is
      // permitted for the user's role. `resolveDestination` handles both
      // extraction and validation; falls back to the role dashboard on any
      // invalid/absent candidate.
      const dest = resolveDestination(location, user.role);
      navigate(dest, { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050003] flex items-center justify-center p-4 scanline-overlay">
      <div className="w-full max-w-md">
        {/* ASCII Art Header */}
        <div className="text-center mb-8">
          <pre className="text-[#39FF14] text-[10px] font-mono leading-tight hidden sm:block">
{`
   █████╗ ███████╗██╗  ██╗     ██╗  ██╗    ███╗   ███╗███████╗██████╗  ██████╗██╗  ██╗
  ██╔══██╗██╔════╝██║  ██║     ╚██╗██╔╝    ████╗ ████║██╔════╝██╔══██╗██╔════╝██║  ██║
  ███████║███████╗███████║      ╚███╔╝     ██╔████╔██║█████╗  ██████╔╝██║     ███████║
  ██╔══██║╚════██║██╔══██║      ██╔██╗     ██║╚██╔╝██║██╔══╝  ██╔══██╗██║     ██╔══██║
  ██║  ██║███████║██║  ██║     ██╔╝ ██╗    ██║ ╚═╝ ██║███████╗██║  ██║╚██████╗██║  ██║
  ╚═╝  ╚═╝╚══════╝╚═╝  ╚═╝     ╚═╝  ╚═╝    ╚═╝     ╚═╝╚══════╝╚═╝  ╚═╝ ╚═════╝╚═╝  ╚═╝
`}
          </pre>
          <div className="flex items-center justify-center gap-2 mt-4">
            <Terminal className="w-6 h-6 text-[#39FF14]" />
            <span className="text-[#39FF14] font-mono text-xl font-bold neon-text">ASH&MERCH_</span>
          </div>
          <p className="text-[#4B6B52] font-mono text-sm mt-2">// DELIVERY TERMINAL</p>
        </div>

        {/* Login Form */}
        <div className="bg-[#0C0612] border border-[#2A0F3D] p-6">
          <div className="border-b border-[#2A0F3D] pb-4 mb-6">
            <h1 className="text-[#39FF14] font-mono text-lg font-bold uppercase tracking-wider">
              {'>'} LOGIN_
            </h1>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-[rgba(255,59,48,0.1)] border border-[#FF3B30] p-3 text-[#FF3B30] font-mono text-sm" data-testid="login-error">
                [ERROR] {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#6B8E73] font-mono text-xs uppercase tracking-wider">
                EMAIL_ADDRESS
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono placeholder:text-[#4B6B52] focus:border-[#39FF14] focus:ring-[#39FF14] rounded-none"
                placeholder="user@ashandmerch.com"
                required
                data-testid="login-email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-[#6B8E73] font-mono text-xs uppercase tracking-wider">
                PASSWORD
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono placeholder:text-[#4B6B52] focus:border-[#39FF14] focus:ring-[#39FF14] rounded-none pr-10"
                  placeholder="••••••••"
                  required
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#4B6B52] hover:text-[#39FF14]"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#39FF14] text-[#050003] font-bold hover:bg-[#4AF626] hover:shadow-[0_0_12px_rgba(57,255,20,0.6)] transition-all uppercase tracking-wider rounded-none h-12"
              data-testid="login-submit"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="cursor-blink">█</span> AUTHENTICATING...
                </span>
              ) : (
                '> EXECUTE LOGIN'
              )}
            </Button>
          </form>

          <div className="mt-6 pt-6 border-t border-[#2A0F3D] text-center">
            <p className="text-[#4B6B52] font-mono text-xs mb-2">
              <Link to="/forgot-password" className="text-[#A3C7A8] hover:text-[#39FF14] hover:underline" data-testid="forgot-password-link">
                FORGOT_PASSWORD?
              </Link>
            </p>
            {/* Phase 7-lite — hidden per operator request to remove the
                mis-tap zone next to the PWA install pill. New accounts now
                arrive via QR referral (auto-redirects to /register with the
                code) or the header INSTALL pill's "Create Account" path.
                Direct /register URL still works. */}
            {/* Phase 6.0 — Privacy / Terms links hidden at operator request
                pending legal review. Routes /privacy and /terms still resolve
                for direct access; just removed from the discoverable footer.
            <p className="text-[#4B6B52] font-mono text-[10px] mt-3">
              <Link to="/privacy" className="hover:text-[#39FF14] hover:underline" data-testid="privacy-link">PRIVACY</Link>
              {' · '}
              <Link to="/terms" className="hover:text-[#39FF14] hover:underline" data-testid="terms-link">TERMS</Link>
            </p>
            */}
          </div>
        </div>

        {/* Demo Credentials block — gated behind REACT_APP_SHOW_DEMO_CREDS so
            it appears only in dev/preview, never on the live production app.
            Set REACT_APP_SHOW_DEMO_CREDS=true in preview .env to bring it back. */}
        {process.env.REACT_APP_SHOW_DEMO_CREDS === 'true' && (
          <div className="mt-4 p-4 bg-[#0C0612] border border-[#2A0F3D]" data-testid="demo-credentials">
            <p className="text-[#6B8E73] font-mono text-xs uppercase tracking-wider mb-2">// DEMO CREDENTIALS</p>
            <div className="text-[#A3C7A8] font-mono text-xs space-y-1">
              <p>CUSTOMER: customer@ashandmerch.com / customer123</p>
              <p>DRIVER: driver@ashandmerch.com / driver123</p>
              <p>DISPATCH: admin@ashandmerch.com / dispatch123</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
